import { useEffect, useState, useCallback, useRef } from 'react';
import { Typography, Stack, Divider, Box, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import _ from 'lodash';
import { useConfiguration } from '../../../providers/ConfigurationContext';
import { useApi } from '../../../providers/ApiContext';
import ChatBox from '../../shared/ChatBox';
import SourceDocument from './SourceDocument';
import {
  addChatItem,
  getChatItems,
  updateChatItem,
} from '../../../actions/chat';
import LoadingMask from '../../shared/LoadingMask';
import ChatEditDialog from '../../shared/ChatEditDialog';
import ChatDeleteDialog from '../../shared/ChatDeleteDialog';
import DocumentTypesField from '../../shared/DocumentTypesField';
import { searchDocs } from '../../../actions/search';
import { generateCompletion } from '../../../actions/chat';
import Suggestions from './Suggestions';
import { countTokens } from '../../../actions/tokenize';
import IncludeHistoryField from '../../shared/IncludeHistoryField';
import buildMessages from '@/components/shared/buildMessages';
import { MODEL_TYPES } from '../../../models/ModelTypes';
import MultiModalPrompt from './MultiModalPrompt';
import { useDocTypes } from '../../../providers/DocTypesContext';
import generateChatTitle from '../../shared/generateChatTitle';
import type {
  Chat as ChatType,
  ChatItem,
  ChatItemCreateRequest,
  ChatItemUpdateRequest,
  EntityId,
} from '@/types';
import type { DocumentType } from '@/types/documentTypes';
import type { FileResult } from '@/types/fileTypes';
import type { ModelConfiguration } from '@/models/modelConfigurations';

interface ChatProps {
  chat: ChatType;
  updateChat: (chat_id: EntityId, update: { name?: string }) => Promise<void>;
  deleteChat: (chat_id: EntityId) => Promise<void>;
}

interface SourceDoc {
  content: string;
  title: string;
  certainty: number;
  start_line?: number;
  end_line?: number;
  [key: string]: unknown;
}

interface GenerateResponse {
  generated_text: string;
  finish_reason: string;
  input_tokens: number;
  sources: SourceDoc[];
}

function Chat({ chat, updateChat, deleteChat }: ChatProps) {
  const configuration = useConfiguration();
  const api = useApi();
  const doc_types = useDocTypes();
  const [prompt, setPrompt] = useState<string>('');
  const [prompt_error, setPromptError] = useState<boolean | string>(false);
  const [chat_history, setChatHistory] = useState<ChatItem[] | null>(null);
  const [source_docs, setSourceDocs] = useState<SourceDoc[]>([]);
  const [generation, setGeneration] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [include_history, setIncludeHistory] = useState<boolean>(true);
  const [documents_filter, setDocumentsFilter] = useState<DocumentType | null>(
    null,
  );
  const [image_id, setImageId] = useState<EntityId | null>(null);
  const [image_data, setImageData] = useState<FileResult | null>(null);
  const stopGenerationRef = useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [suggestions] = useState<string[]>(
    _.sampleSize(
      [
        'What are the post market considerations for security policy?',
        'Can the security risk files be part of the safety risk file?',
        'What are the different signals that can impact cybersecurity risk assessment?',
        'Create a Cybersecurity Incident Response Plan.',
        'What are the ways to do risk assessment for security risks?',
        'What is CVSS?',
        'What is the guidance in the standards for postmarket issue management?',
        'What are the things to keep in mind when creating an SBOM?',
        'How do I establish a coordinated vulnerability disclosure process?',
        'What is the guidance for device security configuration?',
        'What is the SOP guidance for risk controls?',
        'Write a information security policy.',
      ],
      4,
    ),
  );

  // useEffect(() => {
  //   if (doc_types?.length && documents_filter === null) {
  //     setDocumentsFilter(doc_types.find((doc_type) => doc_type.level === 0));
  //   }
  // }, [doc_types, documents_filter, setDocumentsFilter]);

  // Clear sources when chat changes (don't show historical sources)
  useEffect(() => {
    if (chat?.entity_id) {
      setSourceDocs([]);
    }
  }, [chat?.entity_id]);

  // Clear sources when starting a new conversation
  useEffect(() => {
    if (isSearching && source_docs.length === 0) {
      setSourceDocs([]);
    }
  }, [isSearching, source_docs.length]);

  // Function to scroll to bottom of chat content
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  // Robust scroll to bottom that waits for container to be ready
  const scrollToBottomWhenReady = useCallback(() => {
    const attemptScroll = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
        return true;
      }
      return false;
    };

    // Try immediately
    if (attemptScroll()) return;

    // If not ready, try again after a short delay
    setTimeout(() => {
      attemptScroll();
    }, 50);
  }, []);

  // Auto-scroll to bottom when generation updates
  useEffect(() => {
    if (generation) {
      // Use requestAnimationFrame for smooth, responsive scrolling
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [generation, scrollToBottom]);

  // More aggressive scrolling during active generation
  useEffect(() => {
    if (generation && generation.length > 0) {
      // Scroll immediately
      scrollToBottom();

      // Also scroll after a small delay to catch any DOM updates
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [generation, scrollToBottom]);

  // Auto-scroll to bottom when component mounts or chat changes
  useEffect(() => {
    if (chat_history && chat_history.length > 0) {
      // Use a longer delay for initial render to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToBottomWhenReady();
      }, 300);

      // Also try scrolling after a longer delay to catch any late DOM updates
      const backupTimer = setTimeout(() => {
        scrollToBottomWhenReady();
      }, 600);

      return () => {
        clearTimeout(timer);
        clearTimeout(backupTimer);
      };
    }
  }, [chat?.entity_id, chat_history, scrollToBottomWhenReady]);

  // Separate effect for initial chat history load
  useEffect(() => {
    if (chat_history && chat_history.length > 0) {
      // Scroll to bottom when chat history is first loaded
      const timer = setTimeout(() => {
        scrollToBottomWhenReady();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [chat_history, scrollToBottomWhenReady]);

  useEffect(() => {
    if (chat?.entity_id && !chat_history && api) {
      getChatItems(api, chat.entity_id)
        .then((data) => {
          setChatHistory(data);
        })
        .catch((error) => console.log(error));
    }
  }, [chat?.entity_id, chat_history, api]);

  // Set documents_filter based on most recent user chat item
  useEffect(() => {
    if (
      chat_history &&
      chat_history.length > 0 &&
      documents_filter === null &&
      doc_types?.length
    ) {
      // Find the most recent user message (bottom-most in the array)
      const userMessages = chat_history.filter((item) => item.role === 'user');

      if (userMessages.length > 0) {
        const mostRecentUserMessage = userMessages[userMessages.length - 1];

        if (mostRecentUserMessage.doc_search_type) {
          // Find the document type that matches the doc_search_type
          const matchingDocType = doc_types.find(
            (doc_type) =>
              doc_type.code === mostRecentUserMessage.doc_search_type,
          );

          if (matchingDocType) {
            setDocumentsFilter(matchingDocType);
          }
        }
      }
    }
  }, [chat_history, documents_filter, doc_types]);

  // Load source documents from most recent assistant message
  // useEffect(() => {
  //   if (chat_history && chat_history.length > 0 && source_docs.length === 0) {
  //     // Find the most recent assistant message (highest order value)
  //     const assistantMessages = chat_history.filter(
  //       (item) => item.role === 'assistant',
  //     );

  //     if (assistantMessages.length > 0) {
  //       // Sort by order to get the most recent one
  //       const mostRecentAssistantMessage = assistantMessages.sort(
  //         (a, b) => b.order - a.order,
  //       )[0];

  //       if (mostRecentAssistantMessage.sources) {
  //         try {
  //           const sources = JSON.parse(mostRecentAssistantMessage.sources);
  //           setSourceDocs(sources);
  //         } catch (error) {
  //           console.log('Error parsing sources from chat history:', error);
  //         }
  //       }
  //     }
  //   }
  // }, [chat_history, source_docs.length]);

  // Reset searching state when chat changes
  useEffect(() => {
    setIsSearching(false);
    stopGenerationRef.current = false;
  }, [chat?.entity_id]);

  // Generate chat title when conditions are met
  useEffect(() => {
    const generateTitle = async () => {
      // Only generate title if:
      // 1. Chat name is still "New Chat"
      // 2. We have exactly 2 messages (1 user + 1 assistant)
      // 3. The last message is from assistant

      if (
        chat?.name === 'New Chat' &&
        chat_history &&
        chat_history.length >= 2 &&
        chat_history[chat_history.length - 1].role === 'assistant' &&
        api
      ) {
        try {
          const generatedTitle = await generateChatTitle(api, chat_history);
          if (generatedTitle && generatedTitle !== 'New Chat') {
            await updateChat(chat.entity_id, { name: generatedTitle });
          }
        } catch (titleError) {
          console.log('Error generating chat title:', titleError);
        }
      }
    };

    generateTitle().then();
  }, [chat?.name, chat_history, chat?.entity_id, api, updateChat]);

  const addToChatHistory = useCallback(
    async (chat_item: {
      name?: string;
      role: 'user' | 'assistant' | 'system';
      content: string;
      order: number;
      doc_search_type?: string | null;
      image_id?: EntityId | null;
      sources?: string;
      finish_reason?: string | null;
      input_tokens?: number | null;
      generated_tokens?: number | null;
      temperature?: number | null;
    }) => {
      if (!api || !chat?.entity_id) return;
      try {
        const content_tokens = await countTokens(api, chat_item.content);
        // Match original behavior: spread chat_item and add content_tokens
        // The API accepts extra fields (name, order, etc.) and ignores them
        const requestData = {
          ...chat_item,
          content_tokens,
        } as ChatItemCreateRequest;
        const resp = await addChatItem(api, chat.entity_id, requestData);

        if (chat_item.role === 'assistant') {
          setGeneration(null);
        } else {
          setIsSearching(true);
        }
        setChatHistory((prevHistory) => {
          if (!prevHistory) return [resp];
          return [...prevHistory, resp];
        });
      } catch (error) {
        console.error('Error adding chat item:', error);
        throw error;
      }
    },
    [chat?.entity_id, api],
  );

  useEffect(() => {
    if (image_id) {
      setIncludeHistory(false);
    } else {
      setIncludeHistory(true);
    }
  }, [image_id, setIncludeHistory]);

  const generateResponse = useCallback(
    async (
      prompt: string,
      chat_history: ChatItem[] | null | undefined,
    ): Promise<GenerateResponse> => {
      setGeneration('');
      stopGenerationRef.current = false;

      // Cleanup function to reset stop generation
      const cleanup = () => {
        stopGenerationRef.current = false;
        setIsSearching(false);
        // Note: setGeneration(null) is now handled in addToChatHistory for assistant messages
      };

      // Create an AbortController for the HTTP request (only when needed)
      let abortController: AbortController | null = null;

      const model = image_id ? MODEL_TYPES.VISION : MODEL_TYPES.TEXT;

      let docs: SourceDoc[] = [];

      if (model === MODEL_TYPES.TEXT) {
        // Check for stop before expensive searchDocs operation
        if (stopGenerationRef.current) {
          cleanup();
          return {
            generated_text: '',
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: [],
          };
        }

        if (!documents_filter || !api) {
          cleanup();
          return {
            generated_text: '',
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: [],
          };
        }

        const doc_query = include_history
          ? prompt +
            '\n' +
            [...(chat_history || [])]
              .reverse()
              .map((chat_line) => chat_line.content)
              .join('\n')
          : prompt;
        const searchResults = await searchDocs(
          api,
          doc_query,
          configuration.generation.num_docs,
          [documents_filter.entity_id],
        );
        docs = (searchResults as unknown as SourceDoc[]) || [];
        // Check for stop after searchDocs
        if (stopGenerationRef.current) {
          cleanup();
          return {
            generated_text: '',
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: [],
          };
        }
      }
      // Check for stop before buildMessages
      if (stopGenerationRef.current) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      if (!api || !configuration?.model) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      const modelConfig = (configuration.model as { llm: ModelConfiguration })
        .llm;
      if (!modelConfig) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      const messages = await buildMessages(
        api,
        modelConfig,
        prompt,
        docs,
        include_history && chat_history ? chat_history : [],
        model === MODEL_TYPES.VISION && image_data
          ? image_data.base64contents
          : undefined,
      );

      const input_tokens = messages.reduce(
        (accumulator, message) => accumulator + message.content_tokens,
        0,
      );
      // console.log(docs);
      // console.log(messages);

      // Check for stop before generateCompletion
      if (stopGenerationRef.current) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      // Create AbortController now that we're about to make the HTTP request
      abortController = new AbortController();

      // Update cleanup to abort the request
      const originalCleanup = cleanup;
      const cleanupWithAbort = () => {
        originalCleanup();
        if (abortController) {
          abortController.abort();
        }
      };

      if (!chat?.entity_id) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      // Convert Message[] to CompletionRequest['messages'] format
      const completionMessages = messages.map((msg) => ({
        role: msg.role,
        content:
          typeof msg.content === 'string'
            ? msg.content
            : JSON.stringify(msg.content),
      }));

      const response = await generateCompletion(
        api,
        chat.entity_id,
        model,
        completionMessages,
        configuration?.generation?.max_new_tokens,
        configuration?.generation?.temperature,
        configuration?.generation?.top_p,
        abortController.signal,
      );

      // Check for stop after the HTTP request is made
      if (stopGenerationRef.current) {
        cleanupWithAbort();
        setSourceDocs(docs);
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: docs,
        };
      }

      if (!response.body) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'error',
          input_tokens: 0,
          sources: docs,
        };
      }

      const reader = response.body.getReader();
      let line = '',
        finish_reason: string | undefined,
        answer = '';

      try {
        while (true) {
          // Check if generation should be stopped
          if (stopGenerationRef.current) {
            cleanupWithAbort();
            setSourceDocs(docs);
            return {
              generated_text: answer,
              finish_reason: 'cancelled',
              input_tokens,
              sources: docs,
            };
          }

          // Use Promise.race to make stop checks more responsive
          const readPromise = reader.read();
          const stopCheckPromise = new Promise<{
            done: boolean;
            value: Uint8Array | null;
            cancelled: boolean;
          }>((resolve) => {
            const checkStop = () => {
              if (stopGenerationRef.current) {
                resolve({ done: true, value: null, cancelled: true });
              } else {
                setTimeout(checkStop, 50); // Check every 50ms
              }
            };
            checkStop();
          });

          const result = await Promise.race([readPromise, stopCheckPromise]);

          const done = 'done' in result ? result.done : false;
          const value = 'value' in result ? result.value : null;
          const cancelled = 'cancelled' in result ? result.cancelled : false;

          if (cancelled) {
            cleanupWithAbort();
            setSourceDocs(docs);
            return {
              generated_text: answer,
              finish_reason: 'cancelled',
              input_tokens,
              sources: docs,
            };
          }

          if (value) {
            line += new TextDecoder().decode(value);
          }
          if (line.endsWith('\n')) {
            const lines = line
              .trim()
              .split('\n')
              .filter((line) => line);
            for (let i = 0; i < lines.length; i++) {
              const data = lines[i].substring(5).trim();
              if (data === '[DONE]') {
                cleanup();
                setSourceDocs(docs);
                return {
                  generated_text: answer,
                  finish_reason: finish_reason || 'stop',
                  input_tokens,
                  sources: docs,
                };
              } else {
                const result = JSON.parse(data);
                if (result?.choices) {
                  if (result?.choices[0]?.finish_reason) {
                    finish_reason = result?.choices[0]?.finish_reason;
                  }
                  const modelLlm = modelConfig;
                  if (
                    !modelLlm.eos_token.includes(
                      result?.choices[0]?.delta?.content || '',
                    )
                  ) {
                    answer += result?.choices[0]?.delta?.content || '';

                    setGeneration((prevGeneration) => {
                      const newGeneration =
                        (prevGeneration || '') +
                        (result?.choices[0]?.delta?.content || '');
                      // Turn off searching indicator when first token arrives
                      if (prevGeneration === '' && newGeneration !== '') {
                        setIsSearching(false);
                      }
                      // Scroll to bottom immediately after updating generation
                      setTimeout(() => scrollToBottom(), 0);
                      return newGeneration;
                    });
                  }
                } else {
                  if (!result.generated_text) {
                    answer += result.token?.text || '';
                    setGeneration((prevGeneration) => {
                      const newGeneration =
                        (prevGeneration || '') + (result.token?.text || '');
                      // Turn off searching indicator when first token arrives
                      if (prevGeneration === '' && newGeneration !== '') {
                        setIsSearching(false);
                      }
                      // Scroll to bottom immediately after updating generation
                      setTimeout(() => scrollToBottom(), 0);
                      return newGeneration;
                    });
                  }
                }
              }
            }
            line = '';
          }
          if (done) {
            cleanup();
            setSourceDocs(docs);
            return {
              generated_text: answer,
              finish_reason: finish_reason || 'stop',
              input_tokens,
              sources: docs,
            };
          }
        }
      } catch (error) {
        cleanupWithAbort();
        setSourceDocs(docs);

        // Check if the error is due to cancellation
        if (error instanceof Error && error.name === 'AbortError') {
          return {
            generated_text: answer,
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: docs,
          };
        }

        throw error;
      }
    },
    [
      chat?.entity_id,
      configuration?.generation?.max_new_tokens,
      configuration?.generation?.num_docs,
      configuration?.generation?.temperature,
      configuration?.generation?.top_p,
      configuration?.model,
      api,
      documents_filter,
      include_history,
      image_id,
      image_data,
      setIsSearching,
      scrollToBottom,
    ],
  );

  const handlePromptSubmit = useCallback(
    (prompt: string) => {
      // Check if stop was clicked before we even start
      if (stopGenerationRef.current) {
        setGeneration(null);
        setIsSearching(false);
        return;
      }

      // Clear previous sources when starting a new conversation
      setSourceDocs([]);

      addToChatHistory({
        name: 'User',
        role: 'user',
        content: prompt,
        order: chat_history?.length || 0,
        doc_search_type: documents_filter?.code || null,
        image_id: image_id || null,
      })
        .then(() => {
          // Check again after adding user message
          if (stopGenerationRef.current) {
            setGeneration(null);
            setIsSearching(false);
            setPrompt('');
            setPromptError(false);
            return;
          }

          // Scroll to bottom after adding user message
          setTimeout(() => {
            scrollToBottom();
          }, 100);

          // console.log(chat_history);
          // setSourceDocs([]); // This line is now handled by the useEffect above
          generateResponse(prompt, chat_history)
            .then(async (response) => {
              // Check if generation was cancelled
              if (response.finish_reason === 'cancelled') {
                // Only skip if no content was generated
                if (
                  !response.generated_text ||
                  response.generated_text.trim() === ''
                ) {
                  setPrompt('');
                  setPromptError(false);
                  return;
                }
              }
              if (!api) return;
              const generated_tokens = await countTokens(
                api,
                response.generated_text.trim(),
              );
              try {
                await addToChatHistory({
                  name: 'AccQsure',
                  role: 'assistant',
                  content: response.generated_text.trim(),
                  order: (chat_history?.length || 0) + 1,
                  sources: JSON.stringify(response.sources),
                  finish_reason: response.finish_reason,
                  input_tokens: response.input_tokens,
                  generated_tokens,
                  temperature: configuration?.generation?.temperature || null,
                });

                // Scroll to bottom after adding assistant response
                setTimeout(() => {
                  scrollToBottom();
                }, 100);

                if (image_id) {
                  setImageData(null);
                  setImageId(null);
                }
              } catch (error) {
                console.log(error);
                setGeneration(null);
                setIsSearching(false);
              }
            })
            .catch((error) => {
              console.log(error);
              setGeneration(null);
              setIsSearching(false);
            });
          setPrompt('');
          setPromptError(false);
        })
        .catch((error) => {
          console.log(error);
          setGeneration(null);
          setIsSearching(false);
        });
    },
    [
      addToChatHistory,
      chat_history,
      configuration?.generation?.temperature,
      generateResponse,
      documents_filter?.code,
      image_id,
      api,
      scrollToBottom,
    ],
  );

  const handleUpdateChatLine = useCallback(
    async (chat_item_id: EntityId, update: ChatItemUpdateRequest) => {
      if (!api || !chat?.entity_id) return;
      const resp = await updateChatItem(
        api,
        chat.entity_id,
        chat_item_id,
        update,
      );
      setChatHistory((prev) => {
        if (!prev) return [resp];
        const new_chat_history = [...prev];
        const index = new_chat_history.findIndex(
          (item) => item.entity_id === chat_item_id,
        );
        new_chat_history[index] = resp;
        return new_chat_history;
      });
      // Scroll to bottom after updating chat line
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    },
    [chat?.entity_id, api, scrollToBottom],
  );

  if (!chat_history) {
    return <LoadingMask />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        maxHeight: '100%',
        minHeight: 0,
      }}
    >
      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '15px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
          // Ensure no nested scrolling conflicts
          '& *': {
            overflow: 'visible',
          },
        }}
        ref={scrollContainerRef}
      >
        <Grid container spacing={1}>
          <Grid size={12}>
            <Stack direction='row' spacing={1} alignItems={'center'}>
              <Typography variant='h5'>{chat.name}</Typography>
              <IconButton onClick={() => setEditOpen(true)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => setDeleteOpen(true)}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Grid>
          <Grid size={12}>
            <ChatBox
              chat_history={chat_history || []}
              generation={generation || undefined}
              isSearching={isSearching}
              updateChatLine={handleUpdateChatLine}
            />
          </Grid>
          {source_docs.length > 0 ? (
            <Box
              sx={{
                margin: '10px',
              }}
            >
              <Grid size={12}>
                <Divider />
              </Grid>{' '}
              <Grid size={12}>
                <Typography variant='h6'>Sources</Typography>{' '}
              </Grid>
              <Grid container spacing={0} size={12}>
                {source_docs.map((doc, i) => {
                  return (
                    <Grid key={`source_doc_${i}`} size={12}>
                      <SourceDocument doc={doc} />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : undefined}
          {chat_history.length === 0 && documents_filter?.level !== 3 ? (
            <>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={12}>
                <Suggestions
                  suggestions={suggestions}
                  onClickSuggestion={(prompt: string) => {
                    handlePromptSubmit(prompt);
                  }}
                />
              </Grid>
            </>
          ) : undefined}
        </Grid>
      </Box>

      {/* Fixed Prompt Section at Bottom */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          paddingX: '15px',
          minHeight: '180px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <MultiModalPrompt
              chat_id={chat.entity_id}
              image_id={image_id || null}
              setImageId={setImageId}
              setImageData={setImageData}
              prompt={prompt}
              error={prompt_error}
              setPrompt={setPrompt}
              setError={setPromptError}
              onSubmit={() => {
                if (prompt && !prompt_error) {
                  handlePromptSubmit(prompt);
                }
              }}
              generating={generation !== null || isSearching}
              disabled={!documents_filter || generation !== null || isSearching}
              stopGeneration={() => {
                stopGenerationRef.current = true;
              }}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 6 }}>
            <DocumentTypesField
              key={documents_filter?.entity_id || 'no-filter'}
              value={documents_filter}
              setValue={setDocumentsFilter}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 6 }}>
            <IncludeHistoryField
              value={include_history}
              setValue={setIncludeHistory}
              disabled={Boolean(image_id)}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Dialogs - positioned absolutely to prevent viewport overflow */}
      <ChatEditDialog
        key={`edit_${chat.updated_at}`}
        chat={chat}
        open={editOpen}
        updateChat={updateChat}
        handleClose={() => setEditOpen(false)}
      />
      <ChatDeleteDialog
        key={`delete_${chat.updated_at}`}
        chat={chat}
        open={deleteOpen}
        deleteChat={deleteChat}
        handleClose={() => setDeleteOpen(false)}
      />
    </Box>
  );
}

export default Chat;
